import type { ComponentType } from 'react';

import { TerminalStatus } from '@/modules/backoffice/types/terminal';
import type { TerminalRecordStatus } from '@/modules/backoffice/types/terminal';
import {
  LinkIcon,
  UnlinkIcon,
  RegisterIcon as ArchiveIcon,
} from '../../operation-card/operation-icons';

import type { BatchOperationType, BatchUnlinkDrawerProps, DetailsOperationOption } from './batch-unlink-drawer.type';
import type { BatchResultItem } from './batch-unlink-drawer.type';

export type OperationOption = {
  type: BatchOperationType;
  labelKey: string;
  descriptionKey: string;
  Icon: ComponentType;
};

export const getDetailsOperationsForStatus = (status: TerminalRecordStatus): DetailsOperationOption[] => {
  switch (status) {
    case TerminalStatus.AVAILABLE:
      return [
        { type: 'LINK_BOTH', Icon: LinkIcon, labelKey: 'backoffice.terminalsBatchOptionLinkBothLabel', hideDescription: true },
        { type: 'LINK_PAYFAC_ONLY', Icon: LinkIcon, labelKey: 'backoffice.terminalsBatchOptionLinkPayfacLabel', hideDescription: true },
      ];
    case TerminalStatus.LINKED:
      return [
        { type: 'ASSOCIATE_SELLER', Icon: LinkIcon, labelKey: 'backoffice.terminalsBatchOptionAssociateSellerLabel', hideDescription: true },
        { type: 'UNLINK_PAYFAC', Icon: UnlinkIcon, labelKey: 'backoffice.terminalsListActionUnlink', hideDescription: true },
      ];
    case TerminalStatus.ACTIVE:
      return [
        { type: 'ASSOCIATE_SELLER', Icon: LinkIcon, labelKey: 'backoffice.terminalsBatchOptionAssociateOtherSellerLabel', hideDescription: true },
        { type: 'DISASSOCIATE_SELLER', Icon: UnlinkIcon, labelKey: 'backoffice.terminalsBatchOptionDisassociateSellerLabel', hideDescription: true },
      ];
    case TerminalStatus.INACTIVE:
    case TerminalStatus.TEST:
    case TerminalStatus.SUPPORT:
      return [];
    default: {
      const _exhaustive: never = status;
      return [];
    }
  }
};

export const getOptionsForStatus = (status: BatchUnlinkDrawerProps['currentStatus']): OperationOption[] => {
  if (status === TerminalStatus.ACTIVE) {
    return [
      {
        type: 'ASSOCIATE_SELLER',
        labelKey: 'backoffice.terminalsBatchOptionAssociateSellerLabel',
        descriptionKey: 'backoffice.terminalsBatchOptionAssociateSellerDescription',
        Icon: LinkIcon,
      },
      {
        type: 'DISASSOCIATE_SELLER',
        labelKey: 'backoffice.terminalsBatchOptionDisassociateSellerLabel',
        descriptionKey: 'backoffice.terminalsBatchOptionDisassociateSellerDescription',
        Icon: UnlinkIcon,
      },
    ];
  }

  if (status === TerminalStatus.LINKED) {
    return [
      {
        type: 'UNLINK_PAYFAC',
        labelKey: 'backoffice.terminalsBatchOptionUnlinkPayfacLabel',
        descriptionKey: 'backoffice.terminalsBatchOptionUnlinkPayfacDescription',
        Icon: UnlinkIcon,
      },
      {
        type: 'ASSOCIATE_SELLER',
        labelKey: 'backoffice.terminalsBatchOptionAssociateSellerLabel',
        descriptionKey: 'backoffice.terminalsBatchOptionAssociateSellerDescription',
        Icon: LinkIcon,
      },
    ];
  }

  return [
    {
      type: 'LINK_BOTH',
      labelKey: 'backoffice.terminalsBatchOptionLinkBothLabel',
      descriptionKey: 'backoffice.terminalsBatchOptionLinkBothDescription',
      Icon: LinkIcon,
    },
    {
      type: 'LINK_PAYFAC_ONLY',
      labelKey: 'backoffice.terminalsBatchOptionLinkPayfacLabel',
      descriptionKey: 'backoffice.terminalsBatchOptionLinkPayfacDescription',
      Icon: ArchiveIcon,
    },
  ];
};

export const needsConfig = (op: BatchOperationType | null): boolean =>
  op === 'ASSOCIATE_SELLER' || op === 'LINK_PAYFAC_ONLY' || op === 'LINK_BOTH';

export const groupBy = <T,>(items: T[], key: (item: T) => number | null | undefined): Record<number, T[]> => {
  const result: Record<number, T[]> = {};
  for (const item of items) {
    const k = key(item);
    if (k == null) continue;
    if (!result[k]) result[k] = [];
    result[k].push(item);
  }
  return result;
};

const ERROR_STATUS_MAP: Record<string, { code: number; messageKey: string }> = {
  TERMINAL_ALREADY_LINKED: { code: 1, messageKey: 'backoffice.terminalsBatchErrorAlreadyLinked' },
  TERMINAL_NOT_LINKED: { code: 2, messageKey: 'backoffice.terminalsBatchErrorNotLinked' },
  TERMINAL_NOT_FOUND_IN_DB: { code: 3, messageKey: 'backoffice.terminalsBatchErrorNotFound' },
  TERMINAL_NOT_ASSOCIATED: { code: 4, messageKey: 'backoffice.terminalsBatchErrorNotAssociated' },
  TERMINAL_ALREADY_ASSOCIATED: { code: 5, messageKey: 'backoffice.terminalsBatchErrorAlreadyAssociated' },
};

export const getErrorInfo = (status?: string | null): { code: number; messageKey: string } => {
  if (status && ERROR_STATUS_MAP[status]) return ERROR_STATUS_MAP[status];
  return { code: 0, messageKey: 'backoffice.terminalsBatchErrorGeneric' };
};

export const downloadCsv = (items: BatchResultItem[], operationLabel: string, t: (key: string) => string) => {
  const errors = items.filter((r) => !r.success);
  const header = ['serial_pos', 'grupo', 'codigo_erro', 'mensagem'].join(';');
  const rows = errors.map((e) => {
    const { code, messageKey } = getErrorInfo(e.errorStatus);
    return [e.serialNumber, operationLabel, code, t(messageKey)].join(';');
  });
  const content = [header, ...rows].join('\n');
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'erros-operacao.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
